import validator from 'validator';
import xss from 'xss';
import { ERROR_CODES } from '@/constants/constants';
import { Erc20DecimalEnum } from '@/utilities/currencyConvert';
import { cleanAddress } from './functions';

// TODO: If you're working with storing images that are sent through the requests, you'll want to use this function to sanitize the images before storing them in the database.

function isValidUrlForHost(url: string, host: string): boolean {
  const options = {
    protocols: ['http', 'https'],
    host_whitelist: [host],
  };

  return validator.isURL(url, options);
}

export const sanitizeAndReplaceMarkup = (
  content: string,
  searchValue: string | RegExp,
  replaceValue: string
) => {
  const replacedContent = content.replace(searchValue, replaceValue);
  const sanitizedContent = xss(replacedContent, {
    whiteList: {
      span: ['class'],
      br: [''],
      img: ['src', 'alt', 'class'],
      a: ['href', 'target', 'class'],
    },
  });
  return { __html: sanitizedContent };
};

export const sanitizeAndValidateParameters = async (params: any) => {
  return Promise.all(
    params.map(async (param: { value: any; type: string }) => {
      const { value, type } = param;
      if (
        value === undefined &&
        !(
          type === 'text?' ||
          type === 'facebook?' ||
          type === 'instagram?' ||
          type === 'opensea?' ||
          type === 'twitter?' ||
          type === 'number?'
        )
      ) {
        throw new Error(ERROR_CODES.MISSING_PARAMETER);
      }

      switch (type) {
        case 'address': {
          const address = cleanAddress(value);

          if (!validator.isEthereumAddress(String(address))) {
            throw new Error(ERROR_CODES.INVALID_ADDRESS);
          }

          return address;
        }

        case 'currency': {
          const currency =
            Erc20DecimalEnum[value as keyof typeof Erc20DecimalEnum];

          if (!currency) throw new Error(ERROR_CODES.INVALID_PAYMENT_CURRENCY);

          return validator.escape(value);
        }

        case 'signature': {
          if (!validator.isAlphanumeric(value) || value.length !== 132) {
            throw new Error(ERROR_CODES.INVALID_SIGNATURE);
          }
          return validator.escape(value);
        }

        case 'text': {
          return validator.escape(value);
        }

        case 'text?': {
          if (value) {
            return validator.escape(value);
          } else {
            return null;
          }
        }

        case 'number': {
          const numberValue = parseInt(value, 10);
          if (isNaN(numberValue)) {
            throw new Error(ERROR_CODES.INVALID_NUMBER);
          }
          return numberValue;
        }

        case 'number?': {
          if (value) {
            const numberValue = parseInt(value, 10);
            if (isNaN(numberValue)) {
              throw new Error(ERROR_CODES.INVALID_NUMBER);
            }
            return numberValue;
          } else {
            return null;
          }
        }

        case 'facebook?': {
          if (value && !isValidUrlForHost(value, 'facebook.com')) {
            throw new Error(ERROR_CODES.INVALID_FACEBOOK_ADDRESS);
          }
          return value ? value : null;
        }
        case 'instagram?': {
          if (value && !isValidUrlForHost(value, 'instagram.com')) {
            throw new Error(ERROR_CODES.INVALID_INSTAGRAM_ADDRESS);
          }
          return value ? value : null;
        }
        case 'opensea?': {
          if (value && !isValidUrlForHost(value, 'opensea.io')) {
            throw new Error(ERROR_CODES.INVALID_OPENSEA_ADDRESS);
          }
          return value ? value : null;
        }
        case 'twitter?': {
          if (value && !isValidUrlForHost(value, 'twitter.com')) {
            throw new Error(ERROR_CODES.INVALID_TWITTER_ADDRESS);
          }
          return value ? value : null;
        }

        default: {
          throw new Error(ERROR_CODES.UNSUPPORTED_DATA_TYPE);
        }
      }
    })
  );
};
